/**
 * Pure data shapes + filtering/grouping logic for the command palette
 * (CommandPalette.web.tsx). Kept free of React/store/router imports so the
 * matching and grouping rules are unit-testable without mounting anything —
 * see __tests__/components/commands.test.ts.
 */

export type CommandGroupId = 'goTo' | 'map' | 'plans' | 'addresses';

/** Fixed display order — sections render in this order and an empty one is
 *  dropped rather than shown with no rows. */
export const COMMAND_GROUP_ORDER: readonly CommandGroupId[] = ['goTo', 'map', 'plans', 'addresses'];

export const COMMAND_GROUP_LABELS: Record<CommandGroupId, string> = {
  goTo: 'Go to',
  map: 'Map',
  plans: 'Your plans',
  addresses: 'Addresses',
};

export interface CommandItem {
  id: string;
  label: string;
  group: CommandGroupId;
  /** Extra terms matched alongside the label (e.g. an old name, a synonym). */
  keywords?: string[];
  /** Single-letter global shortcut shown at the row's right edge, if any. */
  shortcut?: string;
  run: () => void;
}

export interface CommandSection {
  group: CommandGroupId;
  label: string;
  items: CommandItem[];
}

/** Case-insensitive substring match over label + keywords. An empty/blank
 *  query matches everything (the palette's default, no-query state). */
export function matchesQuery(
  item: Pick<CommandItem, 'label' | 'keywords'>,
  query: string
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (item.label.toLowerCase().includes(q)) return true;
  return (item.keywords ?? []).some((k) => k.toLowerCase().includes(q));
}

/**
 * Filters `items` by `query`, then buckets the matches into sections in
 * COMMAND_GROUP_ORDER, dropping any section left with no rows. Item order
 * within a group is preserved from the input array.
 */
export function groupCommandsForDisplay(items: CommandItem[], query: string): CommandSection[] {
  const filtered = items.filter((item) => matchesQuery(item, query));
  const sections: CommandSection[] = [];
  for (const group of COMMAND_GROUP_ORDER) {
    const groupItems = filtered.filter((item) => item.group === group);
    if (groupItems.length > 0) {
      sections.push({ group, label: COMMAND_GROUP_LABELS[group], items: groupItems });
    }
  }
  return sections;
}

/** Flattens sections back into the single ordered list Up/Down navigation
 *  and the highlighted-index math operate on. */
export function flattenSections(sections: CommandSection[]): CommandItem[] {
  return sections.flatMap((s) => s.items);
}

/** Next highlighted index for Up/Down (dir = -1/+1) with wraparound. Callers
 *  should guard `length === 0` themselves (nothing to highlight). */
export function nextHighlightIndex(current: number, length: number, dir: -1 | 1): number {
  if (length <= 0) return 0;
  return (current + dir + length) % length;
}
